import React, { useEffect, useMemo, useRef } from 'react';
import { useIntl, useLocation } from 'umi';
import { v4 as uuidv4 } from 'uuid';
import Ask from '../../../svg/agent-ask.svg';
import Arrow from '../../../svg/agent-arrow.svg';
import WebSearchIcon from '../../../svg/earth-icon.svg';
// import AnswerLoading from '../AnswerLoading';
import { useRequestController } from '../../../Home/hooks/useRequestController.ts';
import './index.scss';
import { isEmpty, last } from 'lodash-es';
import { parseAgentThink } from '../../utils';
import { useStore } from '@/pages/aigc/hooks/useContext';
import { useSessionId } from '../../hooks/useAgentModalSata';
import classNames from 'classnames';
import { useLocalState } from '../../hooks/useLocalState';

const MayAsk = (props: any) => {
  const {
    question,
    isLastQA,
    onRecommendClick,
    streamingState,
    scrollToBottom,
    answer,
    AnswerLoading,
    messageId,
    retrying
  } = props;
  const location: any = useLocation();
  const {
    query: { template },
  } = location || {};
  const { sessionId: routeSessionId, snapshotId } = useSessionId();
  const { formatMessage } = useIntl();
  // 是否是ds报告会话，不需要展示推荐问题
  const [isDSReportSession] = useLocalState('__dba_is_ds_report_session__', false);
  // 设置猜你想问缓存数据
  const [cacheMayAskData, setCacheMayAskData] = useStore<{
    [key in string]: any;
  }>(routeSessionId + messageId, {
    // 默认为空
    data: []
  });
  const firstKey = useRef(false);
  const dataSource = useRef({});
  const isDsTemplate = (template && template === 'dsReport') || isDSReportSession;
  const { rephrase = '', plan = '' } = useMemo(() => {
    const defaultObj = dataSource.current;
    // 如果已经拿到了数据, 就不需要在计算了
    if (firstKey.current) return defaultObj;
    if (last(answer)?.type === 'error' && last(answer)?.value === 'stop') {
      firstKey.current = true;
      return defaultObj;
    }
    // 检查 think_fold_mode：tool 模式直接放行，agent 模式需有 rephrase
    const thinkItem = answer?.find((item: any) => item?.type === 'think');
    const thinkFoldItem = thinkItem?.value?.find((v: any) => v?.type === 'think_fold_mode');
    const foldValue = thinkFoldItem?.value;

    if (foldValue === 'tool') {
      // tool 模式直接放行
      dataSource.current = { rephrase: '', plan: null };
      firstKey.current = true;
      return { rephrase: '', plan: '' };
    }

    const res = parseAgentThink(answer);
    if (res.rephrase) {
      dataSource.current = res;
      firstKey.current = true;
      return res;
    }
    return { rephrase: '', plan: '' };
  }, [answer]);
  
  const { data, loading } = useRequestController({
    method: 'POST',
    url: `/api/agent_guessnext`,
    refreshDeps: useMemo(
      () => ({
        userInput: question,
        nNum: 3,
        trace_id: uuidv4(),
        rephrase,
        plan: plan ? JSON.stringify(plan) : '',
        "sessionid": routeSessionId
      }),
      [question, rephrase, plan, routeSessionId],
    ),
    cacheKey: `latest_guessnext${routeSessionId}`,
    // 有缓存数据，不允许再次发起请求，每次msgid变更，都重新发起请求，否则会使用缓存数据
    ready:
      (isLastQA && !isEmpty(answer) && firstKey.current && streamingState && !isDsTemplate && isEmpty(cacheMayAskData?.data)),
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
    onFinally: () => {
      const dataMayAskList  = data?.result?.query_json;
      firstKey.current = false;
      dataSource.current={};
      setCacheMayAskData({ data: dataMayAskList  });
      !streamingState && !snapshotId && scrollToBottom?.();
    },
  });

  useEffect(() => {
    if (isDsTemplate){
      scrollToBottom?.();
    }
  }, [isDsTemplate]);

  const dataMayAskList = useMemo(() => {
    // 1、先读取接口数据
    if (data?.result?.query_json) {
      return data?.result?.query_json;
    }
    // 2、再读取缓存数据，get操作
    return cacheMayAskData?.data || [];
  }, [cacheMayAskData?.data, data?.result?.query_json]);

  // 这个请求还在加载中，但是我们流式数据还没中断。直接不显示loading
  if ((loading || (isEmpty(dataMayAskList) && !data?.pending && isLastQA)) && !streamingState || ((loading) && streamingState === 'complete')) {
    return <AnswerLoading />;
  }
  
  // 最后一个QA，或者流式数据还在加载中，直接返回null，加载完成直接出现数据
  if (!isLastQA || isEmpty(dataMayAskList) || (streamingState && ['progressing', 'pending'].includes(streamingState)) || retrying || isDsTemplate) {
    return null;
  }
  return (
    <div className="dba-may-ask">
      <div className="dba-ask-title">
        <img src={Ask} />
        <span>{formatMessage({ id: 'dba_may_also_ask' })}</span>
      </div>
      <div className="dba-ask-content">
        {dataMayAskList
          ?.filter((item: any) => item?.content)
          .map((item: any, index: number) => (
            <div
              key={index}
              className={
                classNames("dba-ask-single", {
                  'dba-ask-single-websearch': item?.tag === 'websearch'
                })
              }
              onClick={() => onRecommendClick(item?.content, item?.tag === 'websearch')}
            >
              <span
                className={classNames("dba-ask-text", { 'dba-ask-text-websearch': item?.tag === 'websearch' })}
              >{item?.display_content || item?.content}</span>
              <img src={item?.tag === 'websearch' ? WebSearchIcon : Arrow} />
            </div>
          ))}
      </div>
    </div>
  );
};

export default MayAsk;
